"use client";

import "@/components/home/spectrum-hero.css";
import { DisputeWellLogo } from "@/components/ui/molecules/OrgLogo";
import { Button } from "@/components/ui/shadcn/button";
import { routes } from "@/lib/routing/routes";

import Link from "next/link";

import { memo } from "react";

/**
 * The one marketing nav row — logo, the three primary links, and the
 * sign-in/sign-up pair. Three mounts compose it: the landing hero's in-flow
 * header, the hero's fixed float twin, and the SiteNav gutter shell every
 * other marketing page uses.
 *
 * The outer class is the mount's to give, never this component's to choose:
 * home.css scopes the hero's scroll-recede to
 * `.dw-home-spectrum-hero .dw-home-spectrum-hero__nav` and hides the link row
 * inside `__float-nav`, and both float mount and hero mount live inside the
 * same hero root — so the float twin must never carry `__nav`.
 *
 * Presentational: the session read stays with the mount, which already holds
 * one, so the home page keeps a single subscription for its nav and its CTA.
 */
export const MarketingNav = memo(function MarketingNav({
    className,
    isAuthenticated,
    priority = false,
    inert = false,
    visible,
}: {
    /** Outer <header> class — `__nav` in flow, `__float-nav` (+ `visible`) fixed. */
    className: string;
    isAuthenticated: boolean;
    /** Eager-load the light logo: true where the nav paints above the fold. */
    priority?: boolean;
    /** True while the mount is off-screen, so its links leave the tab order. */
    inert?: boolean;
    /** The fixed twin's slide state; absent on the in-flow nav. */
    visible?: boolean;
}) {
    return (
        <header className={className} inert={inert} data-visible={visible === undefined ? undefined : visible ? "true" : "false"} data-component="MarketingNav" data-location="src/components/home/marketing-nav.tsx">
            <Link href="/">
                <DisputeWellLogo width={1147} height={221} className="dw-home-spectrum-hero__logo" priority={priority} />
            </Link>
            <nav className="dw-home-spectrum-hero__nav-links" aria-label="Primary">
                <Link href="/features" className="dw-text-strong dw-tabular-nums" data-tone="muted">Features</Link>
                <Link href="/how-to" className="dw-text-strong dw-tabular-nums" data-tone="muted">How to</Link>
                <Link href="/blog" className="dw-text-strong dw-tabular-nums" data-tone="muted">Blog</Link>
            </nav>
            <div className="dw-home-spectrum-hero__nav-actions">
                <Button asChild variant="ghost"><Link href={isAuthenticated ? routes.crm.dashboard : routes.crm.login}>{isAuthenticated ? "Dashboard" : "Sign in"}</Link></Button>
                <Button asChild><Link href={routes.crm.signup}>Start Free Trial</Link></Button>
            </div>
        </header>
    );
});
